import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/dashboard/PageHeader';
import { RatingPrompt } from '@/components/order/RatingPrompt';
import Returns from './Returns';
import toast from 'react-hot-toast';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCart } from '@/store/cart';
import { TrackingModal } from '@/components/order/TrackingModal';
import { ComplaintModal } from '@/components/order/ComplaintModal';

interface Order {
  id: string;
  reference: string;
  productTitle: string;
  productImage?: string;
  amountGBP: number;
  totalGBP?: number;
  deliveryService?: string;
  trackingCode?: string;
  state: string;
  lockerCode?: string;
  trackingUrl?: string;
  paidAt?: string;
  inLockerAt?: string;
  collectedAt?: string;
  itemConfirmed?: boolean;
  autoReleaseAt?: string;
  releasedAt?: string;
}

const DELIVERY_LABELS: Record<string, string> = { locker: 'InPost Locker', standard: 'Royal Mail Tracked', express: 'DPD Express' };

const states: Record<string, { label: string; pill: string; step: number }> = {
  CREATED:       { label: 'Awaiting payment',     pill: 'pill-warn', step: 0 },
  PAID_HELD:     { label: 'Funds in payment hold', pill: 'pill-warn', step: 1 },
  AWAITING_DROP: { label: 'Awaiting drop',        pill: 'pill-warn', step: 1 },
  SHIPPED:       { label: 'En route',             pill: 'pill-warn', step: 2 },
  IN_LOCKER:     { label: 'Ready in locker',      pill: 'pill-gold', step: 3 },
  COLLECTED:     { label: 'Delivered · confirm it',   pill: 'pill-gold', step: 3 },
  RELEASED:      { label: 'Completed',            pill: 'pill-ok',   step: 4 },
  DISPUTED:      { label: 'In dispute',           pill: 'pill-bad',  step: 3 },
  REFUNDED:      { label: 'Refunded',             pill: 'pill-bad',  step: 4 },
  CANCELLED:     { label: 'Cancelled',            pill: 'pill-bad',  step: 0 },
};
/**
 * COLLECTED covers two situations - delivered-but-unconfirmed, and confirmed.
 * Showing one label for both is why confirming an order looked like it did
 * nothing. Neither label mentions the seller - what the other party has been
 * paid is not the buyer's business, and showing it only invites "has he got my
 * money yet?" tickets about something the buyer cannot act on.
 */
const stateMeta = (o: { state: string; itemConfirmed?: boolean }) => {
  if (o.state === 'COLLECTED' && o.itemConfirmed) {
    return { label: 'Confirmed', pill: 'pill-ok', step: 4 };
  }
  return states[o.state] ?? { label: o.state.replace(/_/g, ' ').toLowerCase(), pill: 'pill-warn', step: 0 };
};

const STEPS = ['Paid', 'Shipped', 'In locker', 'Complete'];

const REPORT_REASONS = [
  { v: 'WRONG_ITEM',      label: 'Wrong item sent',        hint: 'I received a different product', needsPhoto: true },
  { v: 'NOT_AS_DESCRIBED',label: 'Not as described',       hint: 'Condition, size or details are off', needsPhoto: true },
  { v: 'EMPTY_PARCEL',    label: 'Empty / item missing',   hint: 'The parcel was empty or short', needsPhoto: true },
  { v: 'DAMAGED',         label: 'Arrived damaged',        hint: 'Damaged in transit or on arrival', needsPhoto: true },
  { v: 'COUNTERFEIT',     label: 'Suspected counterfeit',  hint: 'I think this is a fake', needsPhoto: true },
  { v: 'NOT_RECEIVED',    label: 'Never arrived',          hint: 'I did not receive anything', needsPhoto: false },
] as const;

export default function Orders() {
  // After a cart checkout that redirected through Stripe (3DS / wallet), the
  // inline clear never ran - clear the bag on landing so it doesn't linger.
  const _loc = useLocation();
  const _clearCart = useCart((s) => s.clear);
  useEffect(() => {
    const qp = new URLSearchParams(_loc.search);
    if (qp.get('cartpaid') === '1') { _clearCart(); window.history.replaceState({}, '', _loc.pathname); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'all'|'active'|'completed'>('active');
  const [view, setView] = useState<'orders'|'returns'>('orders');
  const { data: orders = [] } = useQuery<Order[]>({
    queryKey: ['orders'],
    queryFn: async () => (await api.get('/me/orders')).data,
  });

  const [confirmOrder, setConfirmOrder] = useState<Order | null>(null);
  const [matchChecked, setMatchChecked] = useState(false);
  const [conditionPhotos, setConditionPhotos] = useState<File[]>([]);
  const [confirmStars, setConfirmStars] = useState(0);
  const [confirmComment, setConfirmComment] = useState('');
  const [trackOrder, setTrackOrder] = useState<Order | null>(null);
  const [complaintOrder, setComplaintOrder] = useState<Order | null>(null);

  const confirm = useMutation({
    mutationFn: async ({ id, fd }: { id: string; fd: FormData }) =>
      (await api.post(`/orders/${id}/confirm-collection`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data,
    onSuccess: () => {
      toast.success('Thanks - that’s confirmed. Nothing else for you to do.');
      setConfirmOrder(null);
      setConditionPhotos([]);
      setConfirmStars(0);
      setConfirmComment('');
      setMatchChecked(false);
      ['orders', 'reviews-pending'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not confirm receipt.'),
  });

  async function submitConfirm() {
    if (!confirmOrder || !matchChecked) return;
    const fd = new FormData();
    fd.append('itemMatches', 'true');
    // Optional, and plural - photos are evidence for a later dispute.
    conditionPhotos.forEach((f) => fd.append('collectionPhoto', f));

    // Rating rides along with the confirmation. Asked here because this is the
    // moment the buyer has just looked at the item; a separate prompt a day
    // later gets ignored.
    if (confirmStars > 0) {
      await api.post('/reviews', {
        orderId: confirmOrder.id,
        stars: confirmStars,
        comment: confirmComment.trim() || undefined,
      }).catch(() => { /* a failed rating must not block the confirmation */ });
    }
    confirm.mutate({ id: confirmOrder.id, fd });
  }

  // Buyer protection: report a wrong / empty / not-as-described / not-received item.
  const [reportOrder, setReportOrder] = useState<Order | null>(null);
  const [reportReason, setReportReason] = useState<string>('WRONG_ITEM');
  const [reportPhoto, setReportPhoto] = useState<File | null>(null);
  const [reportNotes, setReportNotes] = useState('');
  const reasonMeta = REPORT_REASONS.find((r) => r.v === reportReason);

  const report = useMutation({
    mutationFn: async ({ id, fd }: { id: string; fd: FormData }) =>
      (await api.post(`/orders/${id}/report-problem`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data,
    onSuccess: () => {
      toast.success('Report sent. Your money stays held in escrow while Souqli reviews.');
      setReportOrder(null);
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not send your report.'),
  });

  function submitReport() {
    if (!reportOrder) return;
    if (reasonMeta?.needsPhoto && !reportPhoto) { toast.error('Please add a photo showing the problem.'); return; }
    const fd = new FormData();
    fd.append('reason', reportReason);
    if (reportNotes) fd.append('notes', reportNotes);
    if (reportPhoto) fd.append('photo', reportPhoto);
    report.mutate({ id: reportOrder.id, fd });
  }

  const filtered = orders.filter((o) => {
    if (filter === 'active') return !['RELEASED', 'REFUNDED'].includes(o.state);
    if (filter === 'completed') return ['RELEASED', 'REFUNDED'].includes(o.state);
    return true;
  });

  // A return is a later stage of an order, not a separate thing - so it lives
  // on the same screen behind a tab, rather than in its own menu entry.
  if (view === 'returns') {
    return (
      <div>
        <PageHeader
          eyebrow="BUYER DASHBOARD"
          title="Orders &"
          accent="returns."
          subtitle="Every return you've opened, and where Souqli has got to with it."
        />
        <div className="flex items-center gap-2 mt-6 border-b border-warmline">
          <button onClick={() => setView('orders')}
            className="px-4 py-2.5 text-[14px] border-b-2 -mb-px border-transparent text-mute hover:text-ink transition">
            Orders
          </button>
          <button className="px-4 py-2.5 text-[14px] border-b-2 -mb-px border-gold text-ink font-medium">
            Returns
          </button>
        </div>
        <div className="mt-6"><Returns embedded /></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="BUYER DASHBOARD"
        title="Orders &"
        accent="returns."
        subtitle="Souqli holds your payment until your order is delivered and confirmed. If something is wrong, report it and the money stays protected."
      />

      <div className="flex items-center gap-2 mt-6 border-b border-warmline">
        <button className="px-4 py-2.5 text-[14px] border-b-2 -mb-px border-gold text-ink font-medium">
          Orders
        </button>
        <button onClick={() => setView('returns')}
          className="px-4 py-2.5 text-[14px] border-b-2 -mb-px border-transparent text-mute hover:text-ink transition">
          Returns
        </button>
      </div>

      <div className="mt-6"><RatingPrompt /></div>

      {/* Filter pills */}
      <div className="mt-6 inline-flex bg-cream-deep rounded-full p-1">
        {(['active', 'completed', 'all'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-5 py-2 text-sm rounded-full transition capitalize ${
              filter === f ? 'bg-ink text-cream' : 'text-mute hover:text-ink'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        {filtered.map((o) => {
          const s = stateMeta(o);
          return (
            <div key={o.id} className="card p-5">
              <div className="grid lg:grid-cols-12 gap-5">
                <div className="lg:col-span-5 flex items-center gap-4">
                  <div
                    className="w-20 h-20 notched-sm bg-gradient-to-br from-ink-700 to-ink shrink-0"
                    style={o.productImage ? { backgroundImage: `url(${o.productImage})`, backgroundSize: 'cover' } : undefined}
                  />
                  <div className="min-w-0">
                    <div className="stamp text-mute mono">{o.reference}</div>
                    <div className="display text-xl truncate">{o.productTitle}</div>
                    <div className="text-xs text-mute mt-0.5">
                      Paid {o.paidAt ? new Date(o.paidAt).toLocaleDateString() : '-'}
                      {o.deliveryService && <> · {DELIVERY_LABELS[o.deliveryService] ?? o.deliveryService}</>}
                    </div>
                    {o.trackingCode && (
                      <div className="text-[11px] text-mute mt-0.5">Tracking: <span className="mono text-ink">{o.trackingCode}</span></div>
                    )}
                  </div>
                </div>

                <div className="lg:col-span-2 flex flex-col gap-1.5">
                  <span className={`pill ${s.pill} self-start`}>{s.label}</span>
                  <div className="display text-xl">£{(o.totalGBP ?? o.amountGBP).toFixed(2)}</div>
                  <div className="text-[10px] text-mute">incl. fees</div>
                </div>

                <div className="lg:col-span-2 flex items-center">
                  {o.state === 'IN_LOCKER' && o.lockerCode && (
                    <div className="bg-gold/10 border border-gold/30 rounded-luxe px-3 py-2 text-center w-full">
                      <div className="stamp text-gold-dark">Locker PIN</div>
                      <div className="display text-2xl text-gold-dark mono mt-0.5">{o.lockerCode}</div>
                    </div>
                  )}
                </div>

                <div className="lg:col-span-3 flex flex-col gap-1.5 justify-center">
                  {['IN_LOCKER', 'SHIPPED', 'COLLECTED'].includes(o.state) && !o.itemConfirmed && (
                    <button onClick={() => { setConfirmOrder(o); setMatchChecked(false); setConditionPhotos([]); setConfirmStars(0); setConfirmComment(''); }} className="btn btn-sm btn-primary">
                      ✓ Confirm it arrived
                    </button>
                  )}
                  {!['RELEASED', 'REFUNDED', 'CANCELLED', 'CREATED', 'DISPUTED'].includes(o.state) && (
                    <button onClick={() => { setReportOrder(o); setReportReason('WRONG_ITEM'); setReportPhoto(null); setReportNotes(''); }} className="btn btn-sm btn-outline">
                      Report a problem
                    </button>
                  )}
                  {o.state === 'DISPUTED' && (
                    <span className="pill pill-bad self-start text-[9px]">Under review · funds held</span>
                  )}
                  {!['CREATED', 'CANCELLED'].includes(o.state) && (
                    <button onClick={() => setTrackOrder(o)} className="btn btn-sm btn-ghost border border-warmline text-center">
                      📍 Track parcel
                    </button>
                  )}
                  <button onClick={() => setComplaintOrder(o)} className="text-[11px] text-mute hover:text-bad text-center">Report seller</button>
                </div>
              </div>

              {/* Inline timeline */}
              <div className="mt-5 pt-4 border-t border-warmline">
                <div className="flex items-center justify-between gap-2">
                  {STEPS.map((label, i) => {
                    const reached = s.step > i;
                    const current = s.step === i + 1;
                    return (
                      <div key={label} className="flex-1 flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full grid place-items-center stamp transition ${
                          reached ? 'bg-ok text-cream' :
                          current ? 'bg-gold text-ink animate-pulseRing' :
                          'bg-cream-deep text-mute'
                        }`}>
                          {reached ? '✓' : i + 1}
                        </div>
                        <div className={`text-xs ${current ? 'text-ink font-medium' : 'text-mute'}`}>{label}</div>
                        {i < STEPS.length - 1 && <div className={`flex-1 h-px ${reached ? 'bg-ok' : 'bg-warmline'}`} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
        {!filtered.length && (
          <div className="card p-10 text-center">
            <div className="display text-2xl text-mute">No {filter} orders</div>
            <p className="text-sm text-mute mt-2">When you buy something, it'll show up here with full lifecycle tracking.</p>
          </div>
        )}
      </div>

      {/* Receipt confirmation.
          This no longer pays anyone: Souqli releases escrow on a timer once the
          dispute window closes. Confirming just shortens that wait, so the copy
          says so rather than implying the buyer is handing over the money. */}
      {confirmOrder && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink/60 backdrop-blur-sm" onClick={() => setConfirmOrder(null)}>
          <div className="card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="stamp text-gold-dark">CONFIRM IT ARRIVED</div>
            <h2 className="display text-2xl mt-1">{confirmOrder.productTitle}</h2>
            <p className="text-sm text-mute mt-2 leading-relaxed">
              Souqli holds your payment either way - confirming just tells us the order is fine
              and closes it. If you do nothing, we’ll treat it as fine after{' '}
              <span className="font-medium text-ink">2 days</span>.
            </p>
            <p className="text-[13px] text-mute mt-2 leading-relaxed">
              If anything is wrong, close this and use <span className="font-medium text-ink">Report a problem</span> instead -
              your money stays held while Souqli looks at it.
            </p>

            <label className="flex items-start gap-2.5 mt-5 text-sm cursor-pointer">
              <input type="checkbox" className="mt-0.5 accent-gold" checked={matchChecked} onChange={(e) => setMatchChecked(e.target.checked)} />
              <span>The item I collected matches the listing{confirmOrder.trackingCode ? '' : ''} - correct product, brand and condition.</span>
            </label>

            <label className="mt-4 flex items-center gap-3 p-3 border-2 border-dashed border-warmline rounded-luxe cursor-pointer hover:border-gold transition">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => setConditionPhotos(Array.from(e.target.files ?? []).slice(0, 5))}
              />
              <span className="text-xl">📷</span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium truncate">
                  {conditionPhotos.length
                    ? `${conditionPhotos.length} photo${conditionPhotos.length === 1 ? '' : 's'} added`
                    : 'Add photos of what arrived'}
                </div>
                <div className="text-[11px] text-mute">
                  {conditionPhotos.length ? 'Tap to replace' : 'Optional - up to 5. Your proof if you dispute later.'}
                </div>
              </div>
              {!!conditionPhotos.length && <span className="pill pill-ok text-[9px]">Added</span>}
            </label>

            {/* Rating asked here, while they're looking at the item. A separate
                prompt tomorrow gets ignored. */}
            <div className="mt-4">
              <div className="stamp text-[10px] text-mute mb-1.5">RATE THE SELLER (OPTIONAL)</div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    aria-label={`${n} star${n > 1 ? 's' : ''}`}
                    onClick={() => setConfirmStars(n)}
                    className={`text-[26px] leading-none transition hover:scale-110 ${n <= confirmStars ? 'text-gold' : 'text-warmline'}`}
                  >
                    ★
                  </button>
                ))}
                {confirmStars > 0 && (
                  <span className="text-[12px] text-mute ml-2">
                    {['', 'Poor', 'Not great', 'OK', 'Good', 'Excellent'][confirmStars]}
                  </span>
                )}
              </div>

              {confirmStars > 0 && (
                <div className="mt-3">
                  <textarea
                    className="input w-full h-20 resize-none text-[13px]"
                    maxLength={1000}
                    placeholder={confirmStars >= 4
                      ? 'What was good about it? Packaging, accuracy, speed…'
                      : 'What let it down? Other buyers find this the most useful part.'}
                    value={confirmComment}
                    onChange={(e) => setConfirmComment(e.target.value)}
                  />
                  <p className="text-[11px] text-mute mt-1">
                    Optional. Shown on the seller’s profile once you’ve both rated each other.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              <button onClick={() => setConfirmOrder(null)} className="btn btn-outline flex-1">Cancel</button>
              <button
                onClick={submitConfirm}
                disabled={!matchChecked || confirm.isPending}
                className="btn btn-primary flex-1"
              >
                {confirm.isPending ? 'Saving…' : 'Confirm it arrived'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report a problem - buyer protection recourse (opens a dispute, holds funds) */}
      {reportOrder && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink/60 backdrop-blur-sm" onClick={() => setReportOrder(null)}>
          <div className="card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="stamp text-bad">Report a problem</div>
            <h2 className="display text-2xl mt-1">{reportOrder.productTitle}</h2>
            <p className="text-sm text-mute mt-2">
              Tell us what went wrong. Your payment stays safely held in escrow - the seller is not paid - while Souqli reviews your report.
            </p>

            <div className="mt-5 space-y-1.5">
              {REPORT_REASONS.map((r) => (
                <label key={r.v} className={`flex items-start gap-2.5 p-2.5 rounded-luxe border cursor-pointer transition ${reportReason === r.v ? 'border-gold bg-gold/8' : 'border-warmline hover:border-gold/50'}`}>
                  <input type="radio" name="reportReason" className="mt-0.5 accent-gold" checked={reportReason === r.v} onChange={() => setReportReason(r.v)} />
                  <span>
                    <span className="text-sm font-medium">{r.label}</span>
                    <span className="block text-[11px] text-mute">{r.hint}</span>
                  </span>
                </label>
              ))}
            </div>

            {reasonMeta?.needsPhoto && (
              <label className="mt-4 flex items-center gap-3 p-3 border-2 border-dashed border-warmline rounded-luxe cursor-pointer hover:border-gold transition">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setReportPhoto(e.target.files?.[0] ?? null)} />
                <span className="text-xl">📷</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{reportPhoto ? reportPhoto.name : 'Add a photo of the problem'}</div>
                  <div className="text-[11px] text-mute">{reportPhoto ? 'Tap to replace' : 'Required - helps us resolve it fast'}</div>
                </div>
                {reportPhoto && <span className="pill pill-ok text-[9px]">Added</span>}
              </label>
            )}

            <textarea
              className="input min-h-[80px] mt-3"
              placeholder="Add any details (optional)…"
              maxLength={2000}
              value={reportNotes}
              onChange={(e) => setReportNotes(e.target.value)}
            />

            <div className="flex gap-2 mt-6">
              <button onClick={() => setReportOrder(null)} className="btn btn-outline flex-1">Cancel</button>
              <button onClick={submitReport} disabled={report.isPending} className="btn flex-1 bg-bad text-cream hover:opacity-90">
                {report.isPending ? 'Sending…' : 'Submit report'}
              </button>
            </div>
            <p className="text-[11px] text-mute text-center mt-3">False reports may affect your account. Genuine issues are always protected.</p>
          </div>
        </div>
      )}

      {trackOrder && (
        <TrackingModal orderId={trackOrder.id} title={trackOrder.productTitle} onClose={() => setTrackOrder(null)} />
      )}
      {complaintOrder && (
        <ComplaintModal orderId={complaintOrder.id} against="seller" onClose={() => setComplaintOrder(null)} />
      )}
    </div>
  );
}
